import {
  IGartnerEntityFields,
  TBattery,
} from "../../../../pages/Dashboard/Dashboard.type";

export const GARTNER_CURRENT_COMPLETED_YEAR = 2025;

/** Ordered Gartner phases used as battery maturity levels (1..5). */
export const GARTNER_BATTERY_PHASES = [
  "Запуск технологического тренда",
  "Пик завышенных ожиданий",
  "Коррекция ожиданий",
  "Фаза осознанного применения",
  "Плато продуктивности",
] as const satisfies readonly TBattery[];

export type TGartnerBatteryPhase = (typeof GARTNER_BATTERY_PHASES)[number];

export const GARTNER_PHASE_EN: Record<TGartnerBatteryPhase, string> = {
  "Запуск технологического тренда": "Innovation Trigger",
  "Пик завышенных ожиданий": "Peak of Inflated Expectations",
  "Коррекция ожиданий": "Trough of Disillusionment",
  "Фаза осознанного применения": "Slope of Enlightenment",
  "Плато продуктивности": "Plateau of Productivity",
};

/** Maps known phase_2025 strings (RU/EN/legacy) → battery level 1..5. */
export const GARTNER_PHASE_TO_LEVEL: Record<string, number> = {
  "Запуск технологического тренда": 1,
  "Innovation Trigger": 1,
  "Technology Trigger": 1,
  "Пик завышенных ожиданий": 2,
  "Peak of Inflated Expectations": 2,
  "Коррекция ожиданий": 3,
  "Trough of Disillusionment": 3,
  "Отрезвление рынка и развеивание иллюзий": 3,
  "Яма разочарования": 3,
  "Впадина разочарования": 3,
  "Спад разочарования": 3,
  "Фаза осознанного применения": 4,
  "Slope of Enlightenment": 4,
  "Плато продуктивности": 5,
  "Plateau of Productivity": 5,
  // Legacy CMS battery values (3-level) → approximate Gartner levels
  Концепт: 1,
  Прототип: 3,
  Внедрение: 5,
};

/** Legacy CMS labels → canonical Gartner RU phase for display. */
export const LEGACY_BATTERY_TO_PHASE: Record<string, TGartnerBatteryPhase> = {
  Концепт: "Запуск технологического тренда",
  Прототип: "Коррекция ожиданий",
  Внедрение: "Плато продуктивности",
};

export const GARTNER_INFO_TEXT =
  "Модель жизненного цикла технологии строится на основе научной и патентной активности в заданной области. По состоянию на последний завершенный год каждой технологии присваивается определенный этап развития:\n" +
  "1. Запуск технологического тренда (Innovation Trigger).\n" +
  "2. Пик завышенных ожиданий (Peak of Inflated Expectations).\n" +
  "3. Коррекция ожиданий (Trough of Disillusionment).\n" +
  "4. Фаза осознанного применения (Slope of Enlightenment).\n" +
  "5. Плато продуктивности (Plateau of Productivity).";

export const GARTNER_HISTOGRAM_FILL_OPACITY = 0.5;

export const GARTNER_CHART_COLORS = {
  scienceBar: "#3B82F6",
  patentsBar: "#FFCE2F",
  h_t_fact: "#FFFFFF",
  h_t_forecast: "#FFFFFF",
  scienceCurve: "#3B82F6",
  patentsCurve: "#FFCE2F",
  referenceLine: "#EF4444",
  axis: "#9CA3AF",
  grid: "rgba(255, 255, 255, 0.12)",
  panelBg: "#22272B",
  panelBorder: "rgba(255, 255, 255, 0.2)",
} as const;

export const isGartnerEligibleEntity = (
  entity?: IGartnerEntityFields | null
): boolean => {
  if (!entity) return false;
  if (entity.molecule_only) return false;
  if (entity.is_generated_from_molecule) return false;
  return Boolean(entity.plots_data && entity.analytics_data);
};

export const formatGartnerLagYears = (value: number): string => {
  const formatted = value.toFixed(1).replace(".", ",");
  return `≈ ${formatted} ${value === 1 ? "год" : value >= 2 && value < 5 ? "года" : "лет"}`;
};

export const formatGartnerGrowthRate = (value: number): string =>
  value.toFixed(3);

export const formatGartnerWeights = (science: number, patents: number): string =>
  `${science.toFixed(2)} / ${patents.toFixed(2)}`;
